export const ROW_HEIGHT = 44
export const HEADER_HEIGHT = 36
export const FOOTER_HEIGHT = 8

export const COLUMNS = [
  { id: 'symbol', label: 'Market', width: 180 },
  { id: 'markPrice', label: 'Mark Price', width: 120 },
  { id: 'indexPrice', label: 'Index Price', width: 120, dim: true },
  { id: 'openInterestCap', label: 'Open Interest Cap', width: 150, dim: true },
  { id: 'maintenanceMargin', label: 'Maintenance Margin', width: 160 },
  { id: 'minFunding', label: 'Min Funding', width: 110 },
  { id: 'maxFunding', label: 'Max Funding', width: 110 },
]

export const TYPE_TABS = [
  { id: 'all', label: 'All' },
  { id: 'favorites', label: 'Favorites' },
  { id: 'perps', label: 'Perps' },
  { id: 'rwa', label: 'RWA' },
]

export const CATEGORY_TABS = [
  { id: 'all', label: 'All' },
  { id: 'NEW', label: 'NEW' },
  { id: 'MAJORS', label: 'Majors' },
  { id: 'DEFI', label: 'Defi' },
  { id: 'EQUITIES', label: 'Equities' },
  { id: 'ETF_INDEX', label: 'ETF/Index' },
  { id: 'FX', label: 'FX' },
  { id: 'COMMODITIES', label: 'Commodities' },
  { id: 'KRW', label: 'KRW' },
  { id: 'PRE_IPO', label: 'Pre IPO' },
  { id: 'COMPUTE', label: 'Compute' },
  { id: 'LAYER_1', label: 'Layer 1' },
  { id: 'LAYER_2', label: 'Layer 2' },
  { id: 'MEMES', label: 'Memes' },
  { id: 'AI', label: 'AI' },
  { id: 'BONDS', label: 'Bonds' },
]

const CATEGORY_ALIASES = {
  ETF_INDEX: ['ETF', 'INDEX', 'ETF_INDEX', 'ETF/INDEX'],
  PRE_IPO: ['PRE_IPO', 'PREIPO', 'PRE-IPO'],
  EQUITIES: ['EQUITIES', 'EQUITY', 'STOCKS', 'STOCK'],
  DEFI: ['DEFI'],
  COMMODITIES: ['COMMODITIES', 'COMMODITY'],
}

export function tokenLogoUrl(token) {
  const logo = String(token?.logo || token?.symbol || '').toLowerCase()
  const ext = String(token?.logo_extension || 'png').replace(/^\./, '')
  if (!logo) return ''
  return `https://assets.lighter.xyz/fe/token/${logo}.${ext}`
}

export function formatUsdPrice(value, decimals) {
  const n = Number(value)
  if (!Number.isFinite(n)) return '-'
  const abs = Math.abs(n)
  const digits = abs >= 1 ? 2 : Number.isFinite(decimals) ? Math.min(decimals, 6) : 4
  return `$${n.toLocaleString('en-US', {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  })}`
}

const OI_CAP_SENTINEL = 281_474_000

export function formatCompactCap(value) {
  const n = Number(value)
  if (!Number.isFinite(n) || n <= 0 || n >= OI_CAP_SENTINEL) return '-'
  const abs = Math.abs(n)
  if (abs >= 1e9) {
    const b = n / 1e9
    return `${Number.isInteger(b) ? b : b.toFixed(b >= 10 ? 0 : 1)}B`
  }
  if (abs >= 1e6) {
    const m = n / 1e6
    return `${Number.isInteger(m) ? m : m.toFixed(m >= 10 ? 0 : 1)}M`
  }
  if (abs >= 1e3) {
    const k = n / 1e3
    return `${Number.isInteger(k) ? k : k.toFixed(k >= 10 ? 0 : 1)}K`
  }
  return String(Math.round(n))
}

export function formatMarginPct(fraction) {
  const n = Number(fraction)
  if (!Number.isFinite(n)) return '-'
  const pct = (n / 10_000) * 100
  const digits = Number.isInteger(pct) ? 0 : 1
  return `${pct.toFixed(digits)}%`
}

export function formatFundingPct(clampBig) {
  const n = Number(clampBig)
  if (!Number.isFinite(n)) return null
  return n / 8
}

export function formatFundingDisplay(value) {
  if (!Number.isFinite(value)) return '-'
  const abs = Math.abs(value).toFixed(4)
  return `${value < 0 ? '-' : ''}${abs}%`
}

export function normalizeRiskMarket(raw, token) {
  const symbol = String(raw.symbol || '').toUpperCase()
  const imf = Number(raw.default_initial_margin_fraction)
  const leverage = imf > 0 ? Math.round(10_000 / imf) : null
  const funding = formatFundingPct(raw.funding_clamp_big)
  const categories = Array.isArray(token?.categories)
    ? token.categories.map((c) => String(c).toUpperCase())
    : []
  const assetType = String(token?.asset_type || '').toUpperCase()

  const cap = Number(raw.order_quote_limit)
  const openInterestCap = cap >= OI_CAP_SENTINEL ? 0 : cap

  return {
    id: raw.market_id,
    symbol,
    leverage,
    markPrice: Number(raw.mark_price),
    indexPrice: Number(raw.index_price),
    openInterestCap,
    maintenanceMargin: Number(raw.maintenance_margin_fraction),
    minFunding: Number.isFinite(funding) ? -funding : null,
    maxFunding: Number.isFinite(funding) ? funding : null,
    priceDecimals: Number(raw.supported_price_decimals ?? raw.price_decimals),
    logoUrl: tokenLogoUrl(token) || `https://assets.lighter.xyz/fe/token/${symbol.toLowerCase()}.png`,
    assetType,
    categories,
    hidden: Boolean(raw.market_config?.hidden),
    status: String(raw.status || ''),
  }
}

export function matchesCategory(market, categoryId) {
  if (!categoryId || categoryId === 'all') return true
  const aliases = CATEGORY_ALIASES[categoryId] || [categoryId]
  return market.categories.some((c) => aliases.includes(c))
}

export function matchesType(market, typeId, favorites) {
  if (typeId === 'favorites') return favorites.has(market.symbol)
  if (typeId === 'rwa') return market.assetType === 'RWA'
  if (typeId === 'perps') return market.assetType !== 'RWA'
  return true
}

export function sortMarkets(markets, { key, direction }) {
  const dir = direction === 'asc' ? 1 : -1
  return [...markets].sort((a, b) => {
    const av = a[key]
    const bv = b[key]
    if (typeof av === 'string' || typeof bv === 'string') {
      return String(av || '').localeCompare(String(bv || '')) * dir
    }
    const an = Number(av)
    const bn = Number(bv)
    const aOk = Number.isFinite(an)
    const bOk = Number.isFinite(bn)
    if (!aOk && !bOk) return 0
    if (!aOk) return 1
    if (!bOk) return -1
    if (an === bn) return String(a.symbol).localeCompare(String(b.symbol))
    return (an - bn) * dir
  })
}
